import { Alert, Button, Heading, HStack, Stack, Text } from '@chakra-ui/react'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import {
  getDemand,
  getMatchingSibs,
  publishToAllMatchingSibs,
  publishToSelectedSibs,
} from '../api'
import { EmptyState } from '../components/EmptyState'
import { ErrorState } from '../components/ErrorState'
import { LoadingState } from '../components/LoadingState'
import { MatchingSibTable } from '../components/MatchingSibTable'
import { PublishActions } from '../components/PublishActions'
import type { Demand, MatchingSib } from '../types'

export function MatchingResultsPage() {
  const { demandId } = useParams<{ demandId: string }>()
  const [demand, setDemand] = useState<Demand | null>(null)

  const [sibs, setSibs] = useState<MatchingSib[]>([])
  const [selectedSibIds, setSelectedSibIds] = useState<string[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isPublishingSelected, setIsPublishingSelected] = useState(false)
  const [isPublishingAll, setIsPublishingAll] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function loadMatchingResults() {
    if (!demandId) return

    try {
      setIsLoading(true)
      setError(null)

      const [demandResult, sibsResult] = await Promise.all([
        getDemand(demandId),
        getMatchingSibs(demandId),
      ])

      setDemand(demandResult)
      setSibs(sibsResult)
    } catch {
      setError('Could not load matching results.')
    } finally {
      setIsLoading(false)
    }
  }

  async function handlePublishSelected() {
    if (!demand || selectedSibIds.length === 0) return

    try {
      setIsPublishingSelected(true)

      await publishToSelectedSibs(demand, selectedSibIds)

      await loadMatchingResults()
    } catch {
      setError('Failed to publish demand to selected SIBs.')
    } finally {
      setIsPublishingSelected(false)
    }
  }

  async function handlePublishAll() {
    if (!demand) return

    try {
      setIsPublishingAll(true)

      await publishToAllMatchingSibs(demand)

      await loadMatchingResults()
    } catch {
      setError('Failed to publish demand to matching SIBs.')
    } finally {
      setIsPublishingAll(false)
    }
  }

  useEffect(() => {
    void loadMatchingResults()
  }, [demandId])

  if (isLoading) {
    return <LoadingState message="Loading matching results..." />
  }

  if (error && sibs.length === 0) {
    return <ErrorState message={error} onRetry={loadMatchingResults} />
  }

  return (
    <Stack gap={6}>
      <Stack gap={2}>
        <Heading size="lg">Matching Results</Heading>

        <Text color="fg.muted">Review matching SIBs and publish the demand.</Text>
      </Stack>

      <Alert.Root status="info">
        <Alert.Indicator />
        <Alert.Content>
          <Alert.Title>Traffic Light Explanation</Alert.Title>

          <Alert.Description>
            Green = capacity fits, Yellow = capacity tight, Red = capacity strongly exceeded.
          </Alert.Description>
        </Alert.Content>
      </Alert.Root>

      {error && (
        <Alert.Root status="error">
          <Alert.Indicator />
          <Alert.Content>
            <Alert.Description>{error}</Alert.Description>
          </Alert.Content>
        </Alert.Root>
      )}

      {sibs.length === 0 ? (
        <EmptyState
          title="No matching SIBs found"
          description="No SIBs were returned by the matching service."
        />
      ) : (
        <>
          <MatchingSibTable
            sibs={sibs}
            selectedSibIds={selectedSibIds}
            onSelectionChange={setSelectedSibIds}
          />

          <PublishActions
            selectedCount={selectedSibIds.length}
            isPublishingSelected={isPublishingSelected}
            isPublishingAll={isPublishingAll}
            onPublishSelected={handlePublishSelected}
            onPublishAll={handlePublishAll}
          />
        </>
      )}
    </Stack>
  )
}
